import { Account } from "features/trading/accounts";
import { Bucket, BucketAsset, BucketNumberEnum } from "features/trading/buckets";

export const MAIN_STAR_COLOR = "#f5b301";

export const BUCKET_DESCRIPTIONS: Record<BucketNumberEnum, string> = {
  [BucketNumberEnum.BUCKET_1]:
    "Primary asset(s) of the account. Star one of them as the main asset.",
  [BucketNumberEnum.BUCKET_2]: "A group of assets under this account.",
  [BucketNumberEnum.BUCKET_3]:
    "Every stock the account holds, filled automatically. Untick an asset to exclude it from calculations.",
};

export const sortMembers = (members: BucketAsset[]): BucketAsset[] =>
  [...members].sort((a, b) => a.symbol.localeCompare(b.symbol));

export interface BucketMemberCounts {
  total: number;
  included: number;
  excluded: number;
}

export const memberCounts = (
  bucket: Bucket | undefined,
): BucketMemberCounts => {
  const total = bucket?.assets.length ?? 0;
  const included =
    bucket?.assets.filter((member) => member.included).length ?? 0;
  return { total, included, excluded: total - included };
};

export const formatMemberCounts = (bucket: Bucket): string =>
  bucket.number === BucketNumberEnum.BUCKET_3
    ? `${memberCounts(bucket).included} included · ${
        memberCounts(bucket).excluded
      } excluded`
    : `${bucket.assets.length} asset(s)`;

export enum MemberHistoryStatusEnum {
  READY = "ready",
  FAILED = "failed",
  PENDING = "pending",
}

// Reports need the pair's AccountHistory; excluded members don't take part
// in reports, so only included ones are counted. A stored generation error
// wins over the live existence check: the PORTFOLIO chain may exist while
// the CP max trade amount part of the same attempt failed — that pair is NOT ready.
// The error is cleared by the next successful attempt (bucket ensure, or
// manual generation / record creation from the history panel).
export const memberHistoryStatus = (
  member: BucketAsset,
): MemberHistoryStatusEnum => {
  if (member.history_error) return MemberHistoryStatusEnum.FAILED;
  return member.has_history
    ? MemberHistoryStatusEnum.READY
    : MemberHistoryStatusEnum.PENDING;
};

export type BucketHistoryCounts = Record<MemberHistoryStatusEnum, number>;

export const historyCounts = (
  bucket: Bucket | undefined,
): BucketHistoryCounts => {
  const counts: BucketHistoryCounts = {
    [MemberHistoryStatusEnum.READY]: 0,
    [MemberHistoryStatusEnum.FAILED]: 0,
    [MemberHistoryStatusEnum.PENDING]: 0,
  };
  bucket?.assets.forEach((member) => {
    if (member.included) counts[memberHistoryStatus(member)] += 1;
  });
  return counts;
};

export const formatAccountLabel = (
  account: Pick<Account, "account_number" | "custodian__name">,
): string =>
  `${account.account_number}${
    account.custodian__name ? ` (${account.custodian__name})` : ""
  }`;
